import React from 'react';
import Icon from 'components/AppIcon';

const CaseTable = ({ cases, selectedCases, setSelectedCases, sortConfig, onSort }) => {
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCases(cases.map(case_ => case_.id));
    } else {
      setSelectedCases([]);
    }
  };

  const handleSelectCase = (caseId, checked) => {
    if (checked) {
      setSelectedCases([...selectedCases, caseId]);
    } else {
      setSelectedCases(selectedCases.filter(id => id !== caseId));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-secondary-100 text-text-secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'text-red-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-green-600';
      default:
        return 'text-text-secondary';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const SortableHeader = ({ label, sortKey }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-50 transition-smooth"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <div className="flex flex-col">
          <Icon 
            name="ChevronUp" 
            size={12} 
            className={`${sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'text-primary' : 'text-secondary-300'}`}
          />
          <Icon 
            name="ChevronDown" 
            size={12} 
            className={`${sortConfig.key === sortKey && sortConfig.direction === 'desc' ? 'text-primary' : 'text-secondary-300'}`}
          />
        </div>
      </div>
    </th>
  );

  // Desktop Table View
  const DesktopTable = () => (
    <div className="hidden lg:block overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-secondary-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedCases.length === cases.length && cases.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
            </th>
            <SortableHeader label="Case Number" sortKey="caseNumber" />
            <SortableHeader label="Client" sortKey="clientName" />
            <SortableHeader label="Matter Type" sortKey="matterType" />
            <SortableHeader label="Status" sortKey="status" />
            <SortableHeader label="Advocate" sortKey="assignedAdvocate" />
            <SortableHeader label="Next Court Date" sortKey="nextCourtDate" />
            <SortableHeader label="Last Activity" sortKey="lastActivity" />
            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-border">
          {cases.map((case_) => (
            <tr key={case_.id} className="hover:bg-secondary-50 transition-smooth">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedCases.includes(case_.id)}
                  onChange={(e) => handleSelectCase(case_.id, e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-3 ${getPriorityColor(case_.priority)}`} />
                  <div>
                    <div className="text-sm font-medium text-text-primary">{case_.caseNumber}</div>
                    <div className="text-xs text-text-secondary">{case_.id}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-text-primary font-medium">{case_.clientName}</div>
                <div className="text-xs text-text-secondary">{case_.clientContact}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-text-primary">{case_.matterType}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(case_.status)}`}>
                  {case_.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                {case_.assignedAdvocate}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                {formatDate(case_.nextCourtDate)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                {formatDate(case_.lastActivity)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center gap-2">
                  <button className="text-primary hover:text-primary-700 transition-smooth">
                    <Icon name="Eye" size={16} />
                  </button>
                  <button className="text-secondary-600 hover:text-secondary-800 transition-smooth">
                    <Icon name="Edit" size={16} />
                  </button>
                  <button className="text-secondary-600 hover:text-secondary-800 transition-smooth">
                    <Icon name="Upload" size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Mobile Card View
  const MobileCards = () => (
    <div className="lg:hidden space-y-4">
      {cases.map((case_) => (
        <div key={case_.id} className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedCases.includes(case_.id)}
                onChange={(e) => handleSelectCase(case_.id, e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <div className={`w-2 h-2 rounded-full ${getPriorityColor(case_.priority)}`} />
              <div>
                <div className="text-sm font-medium text-text-primary">{case_.caseNumber}</div>
                <div className="text-xs text-text-secondary">{case_.id}</div>
              </div>
            </div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(case_.status)}`}>
              {case_.status}
            </span>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-xs text-text-secondary">Client:</span>
              <span className="text-sm text-text-primary font-medium">{case_.clientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-secondary">Matter:</span>
              <span className="text-sm text-text-primary">{case_.matterType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-secondary">Advocate:</span>
              <span className="text-sm text-text-primary">{case_.assignedAdvocate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-secondary">Next Court Date:</span>
              <span className="text-sm text-text-primary">{formatDate(case_.nextCourtDate)}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <span className="text-xs text-text-secondary">
              Last activity: {formatDate(case_.lastActivity)}
            </span>
            <div className="flex items-center gap-3">
              <button className="text-primary hover:text-primary-700 transition-smooth">
                <Icon name="Eye" size={16} />
              </button>
              <button className="text-secondary-600 hover:text-secondary-800 transition-smooth">
                <Icon name="Edit" size={16} />
              </button>
              <button className="text-secondary-600 hover:text-secondary-800 transition-smooth">
                <Icon name="Upload" size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (cases.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Briefcase" size={48} className="mx-auto text-secondary-400 mb-4" />
        <h3 className="text-lg font-medium text-text-primary mb-2">No cases found</h3>
        <p className="text-text-secondary">Try adjusting your search or filter criteria</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border">
      <DesktopTable />
      <MobileCards />
    </div>
  );
};

export default CaseTable;